import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest, getStoreIdFromToken } from '../../utils/api';
import MenuPickerBottomSheet from './menuPickerBottomSheet';
import backIcon from '../../assets/icons/common/back_icon.png';

/**
 * 매장 영수증 프린터 매핑 관리.
 * - 매장 PC client 가 자동 인식해서 등록한 프린터 목록 표시
 * - 별표(사용중) 토글 + 축약이름(alias) 지정
 * - 메뉴↔프린터 매핑은 '상품별 출력 설정' 페이지에서 수행
 * - 프린터 클릭 → MenuPickerBottomSheet(readOnly) 로 그 프린터에 매핑된 메뉴 표시(조회 전용)
 */
const PrinterManager = ({ isOpen, onClose, posType }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [storeId, setStoreId] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [devices, setDevices] = useState([]);        // 1단계: PC(device) 목록
  const [selectedDevice, setSelectedDevice] = useState(null); // 선택된 PC (null=1단계, 값=2단계)
  const [autoEntered, setAutoEntered] = useState(false); // PC 1대라 자동으로 2단계 진입(뒤로가기=닫기)
  const [selectedPrinter, setSelectedPrinter] = useState(null); // 조회 피커 대상 프린터
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKeys, setPickerKeys] = useState([]); // 선택 프린터에 매핑된 menu_keys (조회용)
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimerRef = useRef(null);

  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: true, message, type });
    toastTimerRef.current = setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 2000);
  };

  // 1단계: PC(device) 목록 로딩
  const loadDevices = useCallback(async (sid) => {
    if (!sid) return;
    setIsLoading(true);
    let list = [];
    try {
      const r = await apiRequest(`/stores/${sid}/devices`);
      if (r && r.success) { list = r.devices || []; setDevices(list); }
    } catch (e) {
      console.error('[devices] load 실패:', e);
    }
    setIsLoading(false);
    return list;
  }, []);

  // 2단계: 특정 PC(device)의 프린터 목록 + 매핑 로딩.
  // refresh=true: 그 PC 에 WS 재감지 요청 (느림, 정확). false: DB cache 만 (빠름).
  const loadPrinters = useCallback(async (sid, deviceId, refresh = true) => {
    if (!sid || !deviceId) return;
    setIsLoading(true);
    let printersResp = null;
    if (refresh) {
      try {
        printersResp = await apiRequest(`/stores/${sid}/devices/${deviceId}/printers/refresh`, { method: 'POST' });
      } catch (e) {
        const msg = String(e?.message || '');
        if (msg.includes('client_offline') || msg.includes('503')) {
          showToast('이 PC가 연결돼 있지 않습니다. 마지막 저장된 목록을 표시합니다.', 'error');
        } else if (msg.includes('timeout') || msg.includes('504')) {
          showToast('PC 응답 시간 초과. 마지막 저장된 목록을 표시합니다.', 'error');
        }
      }
    }
    if (!printersResp || !printersResp.success) {
      try {
        printersResp = await apiRequest(`/stores/${sid}/devices/${deviceId}/printers`);
      } catch (e) {
        console.error('[printers] cache fetch 실패:', e);
      }
    }
    if (printersResp && printersResp.success) setPrinters(printersResp.printers || []);
    setIsLoading(false);
  }, []);

  // PC 선택 → 2단계 진입. auto=true 는 PC 1대라 자동 진입(뒤로가기 시 목록 대신 닫기).
  const openDevice = (dev, auto = false, sid = storeId) => {
    setSelectedDevice(dev);
    setAutoEntered(auto);
    setPrinters([]);
    loadPrinters(sid, dev.device_id, !!dev.is_online);
  };
  // 2단계 → 1단계 복귀
  const backToDevices = () => {
    setSelectedDevice(null);
    setAutoEntered(false);
    setPrinters([]);
    loadDevices(storeId);
  };

  // onClose 가 부모 inline arrow 라 매 render 마다 새 ref — stabilize (menuPicker 패턴 모방)
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      // 닫기 애니메이션: animating=false → transform 100% 적용 → 200ms 후 unmount
      setIsAnimating(false);
      const t = setTimeout(() => setIsVisible(false), 220);
      return () => clearTimeout(t);
    }
    setIsVisible(true);
    // 첫 mount 시 transition 트리거 위해 다음 frame 에 animating=true
    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setIsAnimating(true));
    });
    (async () => {
      try {
        const sid = getStoreIdFromToken();
        setStoreId(sid);
        setSelectedDevice(null);
        setAutoEntered(false);
        const devs = await loadDevices(sid);
        // 포스 컴퓨터가 1대뿐이면 선택 단계 생략하고 바로 프린터 목록으로.
        if (Array.isArray(devs) && devs.length === 1) {
          openDevice(devs[0], true, sid);
        }
      } catch (e) {
        console.error('[printers] storeId 조회 실패:', e);
        showToast('매장 정보를 불러오는데 실패했습니다', 'error');
        setIsLoading(false);
      }
    })();
    // history entry 쌓아서 뒤로가기 1회는 PrinterManager 만 닫고 storeSettings 는 유지
    window.history.pushState({ printerManager: true }, '');
    const handlePop = () => {
      // state 가 여전히 자기 marker면 위 modal (menu picker 등) 이 pop 된 것 → 무시
      if (window.history.state?.printerManager) return;
      if (typeof onCloseRef.current === 'function') onCloseRef.current();
    };
    window.addEventListener('popstate', handlePop);
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      window.removeEventListener('popstate', handlePop);
      if (window.history.state?.printerManager) {
        window.history.back();
      }
    };
  }, [isOpen, loadDevices]);

  // 사용중(별표) 토글 — 여러 프린터 동시 사용 가능. 켤 때 서버가 축약이름 자동 '프린터N' 부여.
  const handleToggleInUse = async (printer) => {
    if (!storeId) return;
    try {
      const r = await apiRequest(`/stores/${storeId}/printers/${printer.id}/in-use`, {
        method: 'PUT', body: JSON.stringify({ in_use: !printer.in_use }),
      });
      if (r.success) {
        showToast(printer.in_use ? '사용 해제됨' : `사용중: ${r.alias || printer.name}`);
        loadPrinters(storeId, selectedDevice?.device_id, false);
      }
    } catch (e) {
      showToast('사용 설정 실패: ' + e.message, 'error');
    }
  };

  // 프린터 클릭 → 그 프린터에 매핑된 메뉴 조회(읽기 전용). 매핑은 상품별 출력설정에서만 편집.
  const openPicker = async (printer) => {
    setSelectedPrinter(printer);
    try {
      const r = await apiRequest(`/stores/${storeId}/printers/${printer.id}/menus`);
      setPickerKeys(r.success ? (r.menu_keys || []) : []);
    } catch (e) {
      setPickerKeys([]);
    }
    setPickerOpen(true);
  };

  // 축약이름(alias) 저장 — 입력창 blur 시. 긴 OS 인식이름(name)은 그대로 두고 alias만 별도 관리.
  const handleSaveAlias = async (printer, value) => {
    const alias = String(value || '').trim();
    if (!storeId || alias === (printer.alias || '')) return;
    try {
      const r = await apiRequest(`/stores/${storeId}/printers/${printer.id}/in-use`, {
        method: 'PUT', body: JSON.stringify({ in_use: true, alias }),
      });
      if (r.success) {
        showToast('이름 저장됨');
        loadPrinters(storeId, selectedDevice?.device_id, false);
      }
    } catch (e) {
      showToast('이름 저장 실패: ' + e.message, 'error');
    }
  };

  const kindIcon = (kind) => kind === 'windows' ? '🖨' : kind === 'serial' ? '📟' : kind === 'network' ? '🌐' : '🔌';
  const kindLabel = (kind) => kind === 'windows' ? 'Windows 등록' : kind === 'serial' ? '시리얼 (COM)' : kind === 'network' ? '네트워크 (RAW)' : kind;

  if (!isVisible) return null;

  // 사용중(별표) 프린터와 미사용 프린터 분리 — 별표 지정 시 연결됨 그룹으로 이동(상단 표시).
  const connectedPrinters = printers.filter((p) => p.in_use);
  const unconnectedPrinters = printers.filter((p) => !p.in_use);

  const renderPrinterCard = (p) => (
    <div key={p.id} className={`printer-card ${p.in_use ? 'is-main' : ''}`}>
      <button
        type="button"
        className={`main-star ${p.in_use ? 'on' : 'off'}`}
        title={p.in_use ? '사용중 프린터 (해제하려면 클릭)' : '사용중으로 지정 (여러 개 가능)'}
        onClick={(e) => { e.stopPropagation(); handleToggleInUse(p); }}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path
            d="M12 17.27l4.15 2.51c.76.46 1.69-.22 1.49-1.08l-1.1-4.72 3.67-3.18c.67-.58.31-1.68-.57-1.75l-4.83-.41-1.89-4.46c-.34-.81-1.5-.81-1.84 0L9.19 8.63l-4.83.41c-.88.07-1.24 1.17-.57 1.75l3.67 3.18-1.1 4.72c-.2.86.73 1.54 1.49 1.08L12 17.27z"
            fill={p.in_use ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={p.in_use ? 0 : 1.7}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <div className="printer-card-body" onClick={() => openPicker(p)}>
        <div className="printer-info">
          <div className="printer-name">{p.name}</div>
          <div className="printer-meta">
            <span className="kind">{kindLabel(p.kind)}</span>
            {p.port_or_host && <span className="port">{p.port_or_host}{p.net_port ? `:${p.net_port}` : ''}</span>}
          </div>
          <div className="printer-status">
            <span className={`status ${p.is_online ? 'online' : 'offline'}`}>
              {p.is_online ? '● 온라인' : '● 오프라인'}
            </span>
            <span className="status-sep">ㆍ</span>
            <span className="badge">{p.mapped_menu_count}개 매핑</span>
          </div>
        </div>
        <div className="printer-meta-right">
          {p.in_use && (
            <input
              key={`alias-${p.id}-${p.alias || ''}`}
              className="printer-alias-input"
              defaultValue={p.alias || ''}
              placeholder="축약 이름"
              maxLength={50}
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => handleSaveAlias(p, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            />
          )}
          <span className="arrow">›</span>
        </div>
      </div>
    </div>
  );

  const renderDeviceCard = (d) => (
    <button key={d.device_id} type="button" className="printer-card device-card" onClick={() => openDevice(d)}>
      <div className="printer-card-body" style={{ paddingLeft: 16 }}>
        <div className="printer-info">
          <div className="printer-name">{d.label || d.machine_name || 'PC'}</div>
          <div className="printer-meta">
            {d.machine_name && d.label && <span className="kind">{d.machine_name}</span>}
            <span className="port">프린터 {d.printer_count}대 · 매핑 {d.mapped_count}개</span>
          </div>
          <div className="printer-status">
            <span className={`status ${d.is_online ? 'online' : 'offline'}`}>{d.is_online ? '● 온라인' : '● 오프라인'}</span>
          </div>
        </div>
        <div className="printer-meta-right"><span className="arrow">›</span></div>
      </div>
    </button>
  );

  return (
    <>
    <div className={`printer-manager-overlay ${isAnimating ? 'visible' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="printer-manager-drawer">
        <div className="header">
          <img
            src={backIcon}
            alt="뒤로가기"
            className="back-button"
            onClick={selectedDevice ? (autoEntered ? onClose : backToDevices) : onClose}
          />
          <h3>{selectedDevice && !autoEntered ? (selectedDevice.label || selectedDevice.machine_name || 'PC') : '프린터 매핑'}</h3>
          {selectedDevice ? (
            <button
              className="refresh-btn"
              onClick={() => storeId && loadPrinters(storeId, selectedDevice.device_id, true)}
              disabled={isLoading}
              title="이 PC 의 프린터 목록을 다시 가져옵니다"
            >
              {isLoading ? '인식 중…' : '↻ 새로고침'}
            </button>
          ) : <span style={{ position: 'absolute', right: 18, width: 27 }} />}
        </div>

        <div className="body">
          {isLoading && <div className="empty">불러오는 중...</div>}

          {/* 1단계: PC(device) 목록 */}
          {!selectedDevice && !isLoading && devices.length === 0 && (
            <div className="empty">
              <p>등록된 PC가 없습니다.</p>
              <p className="hint">매장 PC 에서 와우오더 클라이언트를 실행하면 자동으로 등록됩니다.</p>
            </div>
          )}
          {!selectedDevice && !isLoading && devices.length > 0 && (
            <div className="printer-list">
              <div className="group-label">POS 컴퓨터</div>
              {devices.map(renderDeviceCard)}
            </div>
          )}

          {/* 2단계: 선택한 PC 의 프린터 목록 */}
          {selectedDevice && !isLoading && printers.length === 0 && (
            <div className="empty">
              <p>이 PC 에 등록된 프린터가 없습니다.</p>
              <p className="hint">이 PC 에 프린터를 연결한 뒤 새로고침하세요.</p>
            </div>
          )}
          {selectedDevice && !isLoading && printers.length > 0 && (
            <div className="printer-list">
              {connectedPrinters.length > 0 && (
                <>
                  <div className="group-label connected">연결됨</div>
                  {connectedPrinters.map(renderPrinterCard)}
                </>
              )}
              {unconnectedPrinters.length > 0 && (
                <>
                  <div className="group-label unconnected">미연결</div>
                  {unconnectedPrinters.map(renderPrinterCard)}
                </>
              )}
            </div>
          )}
        </div>

        {toast.show && <div className={`toast ${toast.type}`}>{toast.message}</div>}
      </div>
    </div>

    {/* 프린터에 매핑된 메뉴 조회(읽기 전용) — overlay 형제로 렌더(자식이면 stacking/터치 충돌) */}
    <MenuPickerBottomSheet
      isOpen={pickerOpen}
      onClose={() => setPickerOpen(false)}
      storeId={storeId}
      mode="grouped"
      readOnly
      registeredKeys={pickerKeys}
      title={selectedPrinter ? `${selectedPrinter.alias || selectedPrinter.name} 출력 메뉴` : '출력 메뉴'}
    />

      <style jsx>{`
        .printer-manager-overlay {
          position: fixed; inset: 0; background: transparent;
          z-index: 10001; pointer-events: none;
        }
        .printer-manager-drawer {
          position: absolute; top: 0; right: 0; width: 100%; max-width: 540px; height: 100%;
          background: #f7f8fa; display: flex; flex-direction: column;
          transform: translateX(100%); transition: transform 0.25s ease-out;
          pointer-events: auto;
        }
        .printer-manager-overlay.visible .printer-manager-drawer { transform: translateX(0); }
        .header {
          display: flex; align-items: center; justify-content: center;
          position: relative; padding: 18px 20px; flex-shrink: 0;
          background: #fff; border-bottom: 1px solid #e9ecef;
        }
        .header h3 { margin: 0; font-size: 16px; font-weight: 700; color: #212529; }
        .back-button {
          position: absolute; left: 18px;
          width: 27px; height: 27px; cursor: pointer;
        }
        .refresh-btn {
          position: absolute; right: 18px;
          background: #fff; border: 1px solid #ced4da; border-radius: 8px;
          padding: 6px 12px; font-size: 12px; font-weight: 600; color: #495057;
          cursor: pointer;
        }
        .refresh-btn:hover:not(:disabled) { background: #f1f3f5; border-color: #adb5bd; }
        .refresh-btn:disabled { opacity: 0.5; cursor: wait; }
        .body { flex: 1; overflow-y: auto; padding: 16px; }
        .empty { text-align: center; padding: 60px 24px; color: #868e96; }
        .empty .hint { font-size: 13px; margin-top: 12px; color: #adb5bd; }
        .printer-list { display: flex; flex-direction: column; gap: 10px; }
        .group-label {
          font-size: 12px; font-weight: 700; color: #868e96;
          margin: 4px 2px -2px; letter-spacing: 0.2px;
        }
        .group-label.connected { color: #2f9e44; }
        .group-label.unconnected { color: #adb5bd; }
        .group-label:not(:first-child) { margin-top: 14px; }
        .device-card {
          cursor: pointer; text-align: left; padding: 0; font: inherit; color: inherit;
          appearance: none; -webkit-appearance: none;
        }
        .device-card .printer-card-body { width: 100%; }
        .printer-card {
          display: flex; align-items: stretch;
          background: #fff; border: 1px solid #e9ecef; border-radius: 12px;
          width: 100%; overflow: hidden;
          transition: border-color 0.12s;
        }
        .printer-card.is-main { border-color: #f59e0b; box-shadow: 0 0 0 1px #f59e0b inset; }
        .printer-card-body {
          display: flex; align-items: center; gap: 14px; padding: 16px 16px 16px 10px;
          background: transparent; border: none;
          flex: 1; min-width: 0; cursor: pointer; text-align: left;
        }
        .printer-card:hover { border-color: #adb5bd; }
        .printer-card.is-main:hover { border-color: #d97706; }
        .printer-card-body:active { transform: scale(0.995); }
        .main-star {
          background: transparent; border: none; cursor: pointer;
          padding: 0 4px 0 14px; font-size: 22px;
          display: flex; align-items: center; justify-content: center;
        }
        .main-star.on { color: #f59e0b; }
        .main-star.off { color: #cbd5e0; }
        .main-star:hover { background: rgba(245, 158, 11, 0.08); }
        .printer-alias-input {
          width: 76px; flex-shrink: 0; margin: 8px 0;
          font-family: inherit; font-size: 13px; font-weight: 700; color: #b45309;
          padding: 14px 8px; border: 1px solid #f5d58b; border-radius: 8px;
          background: #fffbeb; text-align: center; outline: none;
          box-sizing: border-box;
        }
        .printer-alias-input::placeholder { color: #adb5bd; font-weight: 500; }
        .printer-alias-input:focus { border-color: #f59e0b; background: #fff; }
        .printer-info { flex: 1; min-width: 0; }
        .printer-name {
          font-size: 14px; font-weight: 600; color: #212529;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .printer-meta {
          display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
          margin-top: 4px; font-size: 12px; color: #868e96;
        }
        .printer-meta .kind { color: #495057; font-weight: 500; }
        .printer-meta .port { font-family: 'Pretendard', sans-serif; }
        .printer-status {
          display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
          margin-top: 3px; font-size: 12px;
        }
        .printer-status .status.online { color: #2f9e44; }
        .printer-status .status.offline { color: #adb5bd; }
        .printer-meta-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .badge {
          color: #1971c2; font-size: 12px; font-weight: 600;
        }
        .status-sep { color: #adb5bd; font-size: 12px; }
        .arrow { color: #adb5bd; font-size: 20px; }
        .toast {
          position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
          padding: 10px 18px; border-radius: 22px; font-size: 13px;
          background: #212529; color: #fff; z-index: 2000;
        }
        .toast.error { background: #c92a2a; }
      `}</style>
    </>
  );
};

export default PrinterManager;
